import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  DEFAULT_GLOBAL_MAP_ZOOM,
  panToGlobalMap,
} from '@rsos/utils/panToGlobalMap';
import {
  useResourceRequestRegistryLists,
  type ResourceRequestRow,
} from './adapters';
import { useResourceRequestClock } from './useResourceRequestClock';

/**
 * Requests for a single caller, collapsed to the most recent non-closed
 * match when the adapter is opted in with `getQueueInfo`.
 * Adapters that omit it render every request, unchanged.
 *
 * Returns an empty array when every match for the caller is closed, so a
 * caller whose only requests are closed shows no overview banner at all.
 *
 * Mirrors the same group-by-caller, pick-max-activity shape as
 * {@link getAirAmbulanceStatusByCallerID} in `etlStatusUtils.ts`, which picks
 * a display string instead of a request.
 */
function selectBannerRowsForCaller(
  rows: ResourceRequestRow[],
  selectedCallerID: string,
  nowMs: number,
): ResourceRequestRow[] {
  const forCaller = rows.filter(row => row.callerId === selectedCallerID);

  const withInfo: { row: ResourceRequestRow; activityMs: number }[] = [];
  for (const row of forCaller) {
    if (!row.getQueueInfo) {
      return forCaller;
    }
    const info = row.getQueueInfo(nowMs);
    if (info.category !== 'closed') {
      withInfo.push({ row, activityMs: info.activityMs });
    }
  }

  if (withInfo.length <= 1) {
    return withInfo.map(({ row }) => row);
  }

  const mostRecent = withInfo.reduce((latest, current) =>
    current.activityMs > latest.activityMs ? current : latest,
  );
  return [mostRecent.row];
}

/**
 * Opens the resource panel to a requests detail view and pans the map to
 * its pickup location first when the adapter provides one.
 */
function openBannerRequest(
  row: ResourceRequestRow,
  dispatch: ReturnType<typeof useDispatch>,
): void {
  if (row.pickupLocation) {
    panToGlobalMap(
      row.pickupLocation.latitude,
      row.pickupLocation.longitude,
      DEFAULT_GLOBAL_MAP_ZOOM,
    );
  }

  dispatch(
    setSelectedRequest({ requestId: row.id, detailView: row.adapterId }),
  );
  dispatch(openResourcePanel());
  dispatch(setResourcePanelView(row.adapterId));
}

/**
 * Displays ETL-based status banners for resource requests associated with the
 * currently selected call.
 */
function ResourceRequestStatusBanner(): JSX.Element | null {
  const dispatch = useDispatch();
  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const listSlices = useResourceRequestRegistryLists();
  const nowMs = useResourceRequestClock();

  const bannerNodes = useMemo(() => {
    if (!selectedCallerID) {
      return [];
    }

    return listSlices.flatMap(slice => {
      const bannerRows = slice.rows.filter(row => row.renderBanner);

      return selectBannerRowsForCaller(bannerRows, selectedCallerID, nowMs)
        .map(row => {
          if (!row.renderBanner) {
            return null;
          }
          return (
            <Fragment key={row.key}>
              {row.renderBanner({
                onOpen: () => openBannerRequest(row, dispatch),
              })}
            </Fragment>
          );
        })
        .filter((node): node is JSX.Element => node !== null);
    });
  }, [dispatch, listSlices, nowMs, selectedCallerID]);

  if (bannerNodes.length === 0) {
    return null;
  }

  return <>{bannerNodes}</>;
}

export default ResourceRequestStatusBanner;
